import {
    BarChart,
    BookAIcon,
    FileText,
    FormInput,
    Home,

    Network,
    PanelLeft,
    Sparkles,
    StickyNote,
    Tags,
    Timer
} from "lucide-react"

import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useLicense } from '@/hooks/useLicense'


import { NavMain } from "@/components/sidebar/nav-main"
import { SidebarLogo } from "@/components/sidebar/sidebar-logo"
import { Separator } from "@/components/ui/separator"
import {
    Sidebar,
    SidebarContent,
    SidebarFooter,
    SidebarHeader,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    useSidebar
} from "@/components/ui/sidebar"
import { cn } from "@udecode/cn"
import { useMemo } from "react"


/**
 * Main sidebar component for the application
 */
export function AppSidebar() {
    const { projectId } = useParams();
    const navigate = useNavigate();
    const { hasAIFeatures } = useLicense();
    const location = useLocation();


    /**
     * Define the main navigation items
     */
    const navGroups = useMemo(() => {
        // Group 1: General (Core Access)
        const generalItems = [
            {
                title: "Overview",
                url: `/projects/${projectId}/overview`,
                icon: Home,
                isActive: location.pathname === `/projects/${projectId}/overview`,
            },
            {
                title: "Manuscript",
                url: `/projects/${projectId}/chapters`,
                icon: BookAIcon,
                isActive: location.pathname.includes('/chapters'),
            },
        ];

        // Group 2: Planning (Structure)
        const planningItems = [
            {
                title: "Timeline",
                url: `/projects/${projectId}/timeline`,
                icon: Timer,
                isActive: location.pathname.includes('/timeline'),
            },
            {
                title: "Mind Map",
                url: `/projects/${projectId}/mind-maps`,
                icon: Network,
                isActive: location.pathname.includes('/mind-maps')
            },
        ];

        // Group 3: World & Research (Context)
        const worldItems = [
            {
                title: "Elements",
                url: `/projects/${projectId}/elements`,
                icon: FormInput,
                isActive: location.pathname.includes('/elements'),
            },
            {
                title: "Templates",
                url: `/projects/${projectId}/templates`,
                icon: FileText,
                isActive: location.pathname.includes('/templates') || location.pathname.includes('/form-builder'),
            },
            {
                title: "Tags",
                url: `/projects/${projectId}/tags`,
                icon: Tags,
                isActive: location.pathname.includes('/tags'),
            },
            ...(hasAIFeatures ? [{
                title: "Prompts",
                url: `/projects/${projectId}/ai-prompts`,
                icon: Sparkles,
                isActive: location.pathname.includes('/ai-prompts'),
            }] : []),
            {
                title: "Notes",
                url: `/projects/${projectId}/notes`,
                icon: FileText,
                isActive: location.pathname.includes('/notes'),
            },
            {
                title: "Thoughts",
                url: `/projects/${projectId}/thoughts`,
                icon: StickyNote,
                isActive: location.pathname.includes('/thoughts'),
            },
        ];

        // Group 4: Productivity (Tracking)
        const productivityItems = [
            {
                title: "Progress Tracker",
                url: `/projects/${projectId}/progress`,
                icon: BarChart,
                isActive: location.pathname.includes('/progress'),
            },
        ];

        // Consolidate all items into a single list
        const navItems = [
            ...generalItems,
            ...planningItems,
            ...worldItems,
            ...productivityItems
        ];

        return { navItems };
    }, [projectId, location.pathname]);

    // Memoize the navigation handler to prevent unnecessary re-renders
    const handleNavigate = useMemo(() => (url: string) => navigate(url), [navigate]);

    return (
        <Sidebar collapsible="icon" className="border-none">
            {/* Header section with story switcher */}
            <SidebarHeader className="border-b-0">
                <SidebarLogo />
            </SidebarHeader>

            {/* Main content section */}
            <SidebarContent className="gap-0 scrollbar-hide -mt-2.5">
                {/* Main navigation items */}
                <div className="space-y-2">
                    <NavMain items={navGroups.navItems} onNavigate={handleNavigate} />
                </div>
            </SidebarContent>

            {/* Footer section */}
            <SidebarFooter className="pt-0 border-t-0 p-0">
                <Separator className="bg-sidebar-border w-full" />
                <SidebarMenu className="p-2">
                    <SidebarMenuItem>
                        <CollapseButton />
                    </SidebarMenuItem>
                </SidebarMenu>
            </SidebarFooter>


        </Sidebar>
    )
}

function CollapseButton() {
    const { toggleSidebar, state } = useSidebar()
    const isCollapsed = state === "collapsed"

    return (
        <SidebarMenuButton
            asChild
            tooltip="Toggle Sidebar"
            onClick={toggleSidebar}
            className={cn(
                "h-9 px-3 transition-all duration-300 ease-in-out mb-1.5",
                "group-data-[collapsible=icon]:!h-9 group-data-[collapsible=icon]:!w-9 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:!rounded-md",
                "text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent/50 rounded-2xl"
            )}
        >
            <a className="flex items-center gap-2 cursor-pointer">
                <PanelLeft className={cn("size-4", "text-sidebar-foreground/70 group-hover:text-sidebar-foreground")} />
                {!isCollapsed && <span className="text-sm">Collapse Sidebar</span>}
                <span className="sr-only">Toggle Sidebar</span>
            </a>
        </SidebarMenuButton>
    )
}
